import { ErrorModal } from "@dexteel/mesf-core";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { FieldError } from "react-hook-form";
import { Clip } from "../models/Clip";
import { getClips } from "../repositories/ClipRepository";

type Props = {
	label: string;
	value: number | null;
	onChange?: (value: number | null, clip: Clip | null) => void;
	fieldError?: FieldError | undefined;
	disabled?: boolean;
	required?: boolean;
};

const useClips = (onError?: (error: Error) => void) => {
	return useQuery<Clip[], Error>({
		queryKey: ["clips"],
		queryFn: () => getClips(),
		onError: onError,
	});
};

const getClipLabel = (clip: Clip): string => {
	const partNumber = clip.ClipPartNumber ? `${clip.ClipPartNumber} - ` : "";
	return `${partNumber}${clip.Width}x${clip.Height} - ${clip.HolePattern}`;
};

export const ClipSelector = ({
	label,
	value,
	onChange = () => {},
	fieldError,
	disabled = false,
	required = false,
}: Props) => {
	const [error, setError] = useState("");

	const { data: clips, isLoading } = useClips((error) =>
		setError(error.message),
	);

	const selectedClip = clips?.find((c) => c.ClipId === value) || null;

	return (
		<>
			<Autocomplete
				id="clip-selector"
				fullWidth
				clearOnEscape
				options={clips || []}
				getOptionLabel={getClipLabel}
				disabled={disabled}
				renderInput={(params) => (
					<TextField
						{...params}
						required={required}
						label={label}
						error={!!fieldError}
						helperText={fieldError?.message}
						size="small"
						fullWidth
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{isLoading ? (
										<CircularProgress color="inherit" size={20} />
									) : (
										params.InputProps.endAdornment
									)}
								</>
							),
						}}
					/>
				)}
				value={selectedClip}
				onChange={(event, newValue) => {
					onChange(newValue?.ClipId ?? null, newValue);
				}}
			/>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error loading clips"
			/>
		</>
	);
};
